/**
 * useQuestionnaireState Hook
 *
 * Central state management for the questionnaire
 * Handles:
 * - Loading questionnaire structure
 * - Managing responses
 * - Auto-save functionality
 * - Session management
 * - Navigation state
 */

import { useState, useEffect, useCallback, useRef } from "react";

import { STORAGE_KEYS, AUTO_SAVE } from "../constants";
import type {
  QuestionnaireStructure,
  AssessmentResponse,
  AssessmentSession,
} from "../questionnaire.service";
import { questionnaireService } from "../questionnaire.service";
import { isAssessmentComplete } from "../utils/scoring.utils";

interface UseQuestionnaireStateReturn {
  // Data
  questionnaire: QuestionnaireStructure | null;
  responses: Map<string, AssessmentResponse>;
  session: AssessmentSession | null;

  // Loading states
  loading: boolean;
  saving: boolean;
  submitting: boolean;
  error: string | null;

  // Actions
  setResponse: (
    questionId: string,
    value: number,
    notes?: string,
    saveImmediately?: boolean,
  ) => void;
  saveProgress: () => Promise<void>;
  submitAssessment: () => Promise<void>;
  resetAssessment: () => void;

  // Navigation
  currentCategoryId: string | null;
  setCurrentCategoryId: (categoryId: string) => void;

  // Progress
  completionPercentage: number;
  isComplete: boolean;
}

interface UseQuestionnaireStateOptions {
  userId?: string;
}

export function useQuestionnaireState(
  options: UseQuestionnaireStateOptions = {},
): UseQuestionnaireStateReturn {
  const { userId } = options;
  const [questionnaire, setQuestionnaire] =
    useState<QuestionnaireStructure | null>(null);
  const [responses, setResponses] = useState<Map<string, AssessmentResponse>>(
    new Map(),
  );
  const [session, setSession] = useState<AssessmentSession | null>(null);
  const [currentCategoryId, setCurrentCategoryId] = useState<string | null>(
    null,
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const saveProgressRef = useRef<(() => Promise<void>) | null>(null);

  // Load questionnaire and initialize session on mount
  useEffect(() => {
    initializeQuestionnaire();
  }, []);

  // Auto-save when responses change
  useEffect(() => {
    if (!AUTO_SAVE.ENABLED || responses.size < AUTO_SAVE.MIN_RESPONSES) {
      return;
    }

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer
    autoSaveTimerRef.current = setTimeout(() => {
      saveProgress();
    }, AUTO_SAVE.DEBOUNCE_MS);

    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [responses]);

  // Save to localStorage when responses change
  useEffect(() => {
    if (responses.size > 0) {
      const responsesArray = Array.from(responses.values());
      localStorage.setItem(
        STORAGE_KEYS.RESPONSES,
        JSON.stringify(responsesArray),
      );
    }
  }, [responses]);

  // Save current category to localStorage
  useEffect(() => {
    if (currentCategoryId) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_CATEGORY, currentCategoryId);
    }
  }, [currentCategoryId]);

  /**
   * Initialize questionnaire and session
   */
  const initializeQuestionnaire = async () => {
    try {
      setLoading(true);
      setError(null);

      // Step 1: Load questionnaire structure
      const data = await questionnaireService.getActiveQuestionnaire();
      setQuestionnaire(data);

      // Step 2: Try to restore existing session (with validation)
      restoreSession(data);

      // Step 3: If no session exists, create a new one
      const sessionData = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
      if (!sessionData) {
        console.log("No existing session found, creating new session...");
        const sessionPayload: { version_id: string; user_id?: string } = {
          version_id: data.version.id,
        };
        if (userId) {
          sessionPayload.user_id = userId;
        }
        const newSession =
          await questionnaireService.startAssessmentSession(sessionPayload);
        setSession(newSession);
        localStorage.setItem(
          STORAGE_KEYS.SESSION_ID,
          JSON.stringify(newSession),
        );
        localStorage.setItem(STORAGE_KEYS.STARTED_AT, new Date().toISOString());
        console.log("Session created:", newSession.id);
      }

      // Step 4: Set initial category if not already set
      if (!currentCategoryId && data.categories.length > 0) {
        const firstCategory = [...data.categories].sort(
          (a, b) => a.display_order - b.display_order,
        )[0];
        setCurrentCategoryId(firstCategory.id);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load questionnaire",
      );
      console.error("Error initializing questionnaire:", err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Restore session from localStorage with validation
   */
  const restoreSession = (questionnaireData: QuestionnaireStructure) => {
    try {
      // Restore session
      const sessionData = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
      if (sessionData) {
        setSession(JSON.parse(sessionData));
      }

      // Restore responses - but only valid ones
      const savedResponses = localStorage.getItem(STORAGE_KEYS.RESPONSES);
      if (savedResponses) {
        const responsesArray: AssessmentResponse[] = JSON.parse(savedResponses);

        // Create a Set of valid question IDs for fast lookup
        const validQuestionIds = new Set(
          questionnaireData.questions.map((q) => q.id),
        );

        // Filter responses to only include valid question IDs
        const validResponses = responsesArray.filter((r) =>
          validQuestionIds.has(r.question_id),
        );

        // Log if we filtered out any stale responses
        if (validResponses.length < responsesArray.length) {
          console.log(
            `Filtered out ${responsesArray.length - validResponses.length} stale responses from localStorage`,
          );
        }

        const responsesMap = new Map(
          validResponses.map((r) => [r.question_id, r]),
        );
        setResponses(responsesMap);
      }

      // Restore current category
      const savedCategory = localStorage.getItem(STORAGE_KEYS.CURRENT_CATEGORY);
      if (savedCategory) {
        setCurrentCategoryId(savedCategory);
      }
    } catch (err) {
      console.error("Error restoring session:", err);
    }
  };

  /**
   * Set/update a response for a question
   */
  const setResponse = useCallback(
    (
      questionId: string,
      value: number,
      notes?: string,
      saveImmediately = false,
    ) => {
      setResponses((prev) => {
        const updated = new Map(prev);
        updated.set(questionId, {
          question_id: questionId,
          response_value: value,
          context_text: notes,
        });
        return updated;
      });

      // If immediate save requested, trigger save without debounce
      if (
        saveImmediately &&
        session &&
        responses.size >= AUTO_SAVE.MIN_RESPONSES
      ) {
        // Clear any pending auto-save
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
        }
        // Save immediately using ref to avoid circular dependency
        if (saveProgressRef.current) {
          saveProgressRef.current();
        }
      }
    },
    [session, responses.size],
  );

  /**
   * Save progress to server
   */
  const saveProgress = useCallback(async () => {
    if (!session || responses.size === 0) return;

    try {
      setSaving(true);
      setError(null);

      const responsesArray = Array.from(responses.values());
      await questionnaireService.saveResponses(session.id, responsesArray);
    } catch (err) {
      console.error("Error saving progress:", err);

      // If session not found (404 error), create a new one and retry
      if (
        err instanceof Error &&
        (err.message.includes("Not Found") || err.message.includes("404"))
      ) {
        console.log("Session expired, creating new session...");
        try {
          if (questionnaire) {
            const retryPayload: { version_id: string; user_id?: string } = {
              version_id: questionnaire.version.id,
            };
            if (userId) {
              retryPayload.user_id = userId;
            }
            const newSession =
              await questionnaireService.startAssessmentSession(retryPayload);
            setSession(newSession);
            localStorage.setItem(
              STORAGE_KEYS.SESSION_ID,
              JSON.stringify(newSession),
            );

            // Retry saving with new session
            const responsesArray = Array.from(responses.values());
            await questionnaireService.saveResponses(
              newSession.id,
              responsesArray,
            );
            console.log("Progress saved with new session:", newSession.id);
          }
        } catch (retryErr) {
          console.error("Failed to create new session:", retryErr);
        }
      }
    } finally {
      setSaving(false);
    }
  }, [session, responses, questionnaire]);

  // Update the ref whenever saveProgress changes
  useEffect(() => {
    saveProgressRef.current = saveProgress;
  }, [saveProgress]);

  /**
   * Submit complete assessment
   */
  const submitAssessment = useCallback(async () => {
    if (!questionnaire || !session) {
      setError("Cannot submit: questionnaire or session not initialized");
      return;
    }

    if (
      !isAssessmentComplete(
        questionnaire.questions,
        Array.from(responses.values()),
      )
    ) {
      setError("Please answer all required questions before submitting");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const responsesArray = Array.from(responses.values());
      await questionnaireService.submitAssessment(session.id, responsesArray);

      // Clear localStorage on successful submission
      localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
      localStorage.removeItem(STORAGE_KEYS.RESPONSES);
      localStorage.removeItem(STORAGE_KEYS.CURRENT_CATEGORY);
      localStorage.removeItem(STORAGE_KEYS.STARTED_AT);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to submit assessment",
      );
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [questionnaire, session, responses]);

  /**
   * Reset assessment (start over)
   */
  const resetAssessment = useCallback(() => {
    setResponses(new Map());
    setSession(null);
    setCurrentCategoryId(questionnaire?.categories[0]?.id || null);

    localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    localStorage.removeItem(STORAGE_KEYS.RESPONSES);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_CATEGORY);
    localStorage.removeItem(STORAGE_KEYS.STARTED_AT);
  }, [questionnaire]);

  // Calculate completion percentage
  const completionPercentage = questionnaire
    ? Math.round((responses.size / questionnaire.questions.length) * 100)
    : 0;

  // Check if assessment is complete
  const isComplete = questionnaire
    ? isAssessmentComplete(
        questionnaire.questions,
        Array.from(responses.values()),
      )
    : false;

  return {
    questionnaire,
    responses,
    session,
    loading,
    saving,
    submitting,
    error,
    setResponse,
    saveProgress,
    submitAssessment,
    resetAssessment,
    currentCategoryId,
    setCurrentCategoryId,
    completionPercentage,
    isComplete,
  };
}
